import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

import { authRoles } from 'app/auth';

import ServiceCategoryProvider from '../context/ServiceCategoryContext';
import ServiceProvider from '../context/ServiceContext';
import GroupServiceProvider from '../context/GroupServiceContext';
import ServiceTemplateGuard from '../guards/ServiceTemplateGuard';

import GroupServiceDetailsLayout from '../layouts/GroupServiceDetailsLayout';
import GroupServiceCategoryDetailsLayout from '../layouts/GroupServiceCategoryDetailsLayout';

import GroupServicesLayout from './support/layouts/GroupServicesLayout';
import ServiceDetailsLayout from './support/layouts/ServiceDetailsLayout';

// Screens the Canadian slice owns (they differ from the UK).
const ServiceSettingsPage = lazy(() => import('./support/ServiceSettingsPage'));
const ServiceCreatePage = lazy(() => import('./support/ServiceCreatePage/ServiceCreatePage'));

// Screens identical to the UK today, shared until they diverge.
const PharmacyServiceSchedulePage = lazy(() => import('../use-cases/PharmacyServiceSchedulePage'));
const PharmacyServiceContentPage = lazy(() => import('../use-cases/PharmacyServiceContent'));
const ConsultationRoomsTableView = lazy(() => import('../use-cases/ConsultationRoomsTableView'));

const GroupServicePage = lazy(() => import('../use-cases/GroupServices'));
const GroupServiceCreatePage = lazy(() => import('../use-cases/GroupServiceCreate'));
const GroupServiceSchedulesPage = lazy(() => import('../use-cases/GroupServiceSchedules'));
const GroupServiceSettingsPage = lazy(() => import('../use-cases/GroupServiceSettings'));
const GroupServiceContentPage = lazy(() => import('../use-cases/GroupServiceContent'));
const GroupServicePaymentsPage = lazy(() => import('../use-cases/GroupServicePaymentsPage'));
const GroupServicePharmaciesPage = lazy(() => import('../use-cases/GroupServicePharmacies'));
const BlockOutDaysPage = lazy(() => import('../use-cases/BlockOutDays'));
const BlockOutDaysCreatePage = lazy(() => import('../use-cases/BlockOutDaysCreatePage'));
const BlockOutDaysEditPage = lazy(() => import('../use-cases/BlockOutDaysEditPage'));

const GroupServiceCategoriesPage = lazy(() => import('../use-cases/GroupServiceCategories'));
const GroupServiceCategoryCreatePage = lazy(
  () => import('../use-cases/GroupServiceCategoryCreatePage')
);
const GroupServiceCategoriesSettingsPage = lazy(
  () => import('../use-cases/GroupServiceCategoriesSettingsPage')
);
const GroupServiceCategoriesContentPage = lazy(
  () => import('../use-cases/GroupServiceCategoriesContent')
);

const pharmacyListHeaderSettings = {
  layout: {
    config: {
      healtheraHeader: {
        display: true,
        options: {
          pharmacySelector: true,
          organisationSelector: false,
        },
      },
    },
  },
};

const noSelectorsHeaderSettings = {
  layout: {
    config: {
      healtheraHeader: {
        display: true,
        options: {
          pharmacySelector: false,
          organisationSelector: false,
        },
      },
    },
  },
};

const organisationHeaderSettings = {
  layout: {
    config: {
      healtheraHeader: {
        display: true,
        options: {
          organisationSelector: false,
        },
      },
    },
  },
};

const serviceDetailsHeaderSettings = {
  layout: {
    config: {
      healtheraHeader: {
        display: true,
        options: {
          pharmacySelector: true,
          pharmacySelectorHidden: true,
          organisationSelector: false,
        },
      },
    },
  },
};

// The Canadian composition root for the services experience, shared by all
// pharmacy roles (HEA-1852). It owns the route → view wiring so UK route
// changes never leak into Canada. The `services` and `services/:serviceId`
// paths stay registered by the Canada ServicesConfig. Categories and Group
// services are support-only in Canada (HEA-2112): their routes carry
// support-tier auth, so other roles are bounced by FuseAuthorization instead
// of reaching the pages. Group questionnaires and the service Payment tab do
// not exist in Canada; their URLs redirect (dropping them entirely would fall
// through to the services/:serviceId matcher).
const SupportGroupServicesConfig = {
  settings: {
    layout: {
      config: {
        footer: {
          display: false,
        },
        healtheraHeader: {
          display: true,
          options: {
            organisationSelector: true,
          },
        },
      },
    },
  },
  auth: authRoles.pharmacist,
  routes: [
    {
      path: 'services/service-templates',
      element: (
        <GroupServicesLayout>
          <GroupServicePage />
        </GroupServicesLayout>
      ),
      auth: authRoles.support,
    },
    {
      path: 'services/service-templates/schedules',
      element: (
        <GroupServicesLayout>
          <GroupServiceSchedulesPage />
        </GroupServicesLayout>
      ),
      auth: authRoles.support,
    },
    {
      path: 'services/block-out-days',
      children: [
        {
          path: '',
          element: (
            <GroupServicesLayout>
              <BlockOutDaysPage />
            </GroupServicesLayout>
          ),
        },
        { path: 'create', element: <BlockOutDaysCreatePage /> },
        {
          path: ':blockOutRangeId/edit',
          element: <BlockOutDaysEditPage />,
          settings: {
            layout: {
              config: {
                healtheraHeader: {
                  display: false,
                },
              },
            },
          },
        },
      ],
      settings: pharmacyListHeaderSettings,
    },
    {
      path: 'services/service-templates/categories',
      element: (
        <GroupServicesLayout>
          <GroupServiceCategoriesPage />
        </GroupServicesLayout>
      ),
      auth: authRoles.support,
    },
    {
      path: 'services/group-questionnaires',
      element: <Navigate to="/services" replace />,
    },
    {
      path: 'services/questionnaires/:questionnaireId/builder',
      element: <Navigate to="/services" replace />,
    },
    {
      path: 'services/service-templates/categories/create',
      element: <GroupServiceCategoryCreatePage />,
      settings: noSelectorsHeaderSettings,
      auth: authRoles.support,
    },
    {
      path: 'services/service-templates/categories/:categoryId/settings',
      element: (
        <ServiceCategoryProvider>
          <GroupServiceCategoryDetailsLayout>
            <GroupServiceCategoriesSettingsPage />
          </GroupServiceCategoryDetailsLayout>
        </ServiceCategoryProvider>
      ),
      settings: noSelectorsHeaderSettings,
      auth: authRoles.support,
    },
    {
      path: 'services/service-templates/categories/:categoryId/content',
      element: (
        <ServiceCategoryProvider>
          <GroupServiceCategoryDetailsLayout>
            <GroupServiceCategoriesContentPage />
          </GroupServiceCategoryDetailsLayout>
        </ServiceCategoryProvider>
      ),
      settings: noSelectorsHeaderSettings,
      auth: authRoles.support,
    },
    {
      path: 'services/service-templates/create',
      element: <GroupServiceCreatePage />,
      settings: noSelectorsHeaderSettings,
      auth: authRoles.support,
    },
    {
      path: 'services/service-templates/:serviceTemplateId/organisations/:organisationId/settings',
      element: (
        <GroupServiceProvider>
          <GroupServiceDetailsLayout>
            <GroupServiceSettingsPage />
          </GroupServiceDetailsLayout>
        </GroupServiceProvider>
      ),
      settings: organisationHeaderSettings,
      auth: authRoles.support,
    },
    {
      path: 'services/service-templates/:serviceTemplateId/organisations/:organisationId/content',
      element: (
        <ServiceTemplateGuard>
          <GroupServiceProvider>
            <GroupServiceDetailsLayout>
              <GroupServiceContentPage />
            </GroupServiceDetailsLayout>
          </GroupServiceProvider>
        </ServiceTemplateGuard>
      ),
      settings: organisationHeaderSettings,
      auth: authRoles.support,
    },
    {
      path: 'services/service-templates/:serviceTemplateId/organisations/:organisationId/payments',
      element: (
        <ServiceTemplateGuard>
          <GroupServiceProvider>
            <GroupServiceDetailsLayout>
              <GroupServicePaymentsPage />
            </GroupServiceDetailsLayout>
          </GroupServiceProvider>
        </ServiceTemplateGuard>
      ),
      settings: organisationHeaderSettings,
      auth: authRoles.support,
    },
    {
      path: 'services/service-templates/:serviceTemplateId/organisations/:organisationId/pharmacies',
      element: (
        <ServiceTemplateGuard>
          <GroupServiceProvider>
            <GroupServiceDetailsLayout>
              <GroupServicePharmaciesPage />
            </GroupServiceDetailsLayout>
          </GroupServiceProvider>
        </ServiceTemplateGuard>
      ),
      settings: organisationHeaderSettings,
      auth: authRoles.support,
    },
    {
      path: 'services/consultation-rooms',
      element: (
        <GroupServicesLayout>
          <ConsultationRoomsTableView />
        </GroupServicesLayout>
      ),
      settings: pharmacyListHeaderSettings,
    },
    {
      path: 'services/create',
      element: <ServiceCreatePage />,
      settings: noSelectorsHeaderSettings,
    },
    {
      path: 'services/:serviceId/settings',
      element: (
        <ServiceProvider>
          <ServiceDetailsLayout>
            <ServiceSettingsPage />
          </ServiceDetailsLayout>
        </ServiceProvider>
      ),
      settings: serviceDetailsHeaderSettings,
    },
    {
      path: 'services/:serviceId/content',
      element: (
        <ServiceProvider>
          <ServiceDetailsLayout>
            <PharmacyServiceContentPage />
          </ServiceDetailsLayout>
        </ServiceProvider>
      ),
      settings: serviceDetailsHeaderSettings,
    },
    {
      path: 'services/:serviceId/schedule',
      element: (
        <ServiceProvider>
          <ServiceDetailsLayout>
            <PharmacyServiceSchedulePage />
          </ServiceDetailsLayout>
        </ServiceProvider>
      ),
      settings: serviceDetailsHeaderSettings,
    },
    {
      path: 'services/:serviceId/payments',
      element: <Navigate to="../settings" relative="path" replace />,
    },
  ],
};

export default SupportGroupServicesConfig;
